import {
  Bell,
  BellOff,
  CheckCircle,
  ChevronDown,
  MoreVertical,
  RotateCcw,
  Sparkles,
  Trash2,
  User,
  X,
  RefreshCw,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Switch } from "@/components/ui/switch";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { MemberAssignPopover } from "@/components/conversations/MemberAssignPopover";
import { LeadTagsPopover } from "@/components/conversations/LeadTagsPopover";
import { ConversationLeadFunnelControls } from "@/components/conversations/ConversationLeadFunnelControls";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";

type ConversationForHeader = {
  conversation_id: string;
  lead_id: string;
  lead_name: string;
  channel: string;
  state: string;
  followup_enabled: boolean;
  is_closed?: boolean;
  assigned_member_id?: string | null;
  assigned_member_ids?: string[];
};

type ConversationChatHeaderActionsProps = {
  conversation: ConversationForHeader;
  workspaceId: string;
  memberId: string | null | undefined;
  wide: boolean;
  isSummarizing: boolean;
  isLeadRecovering?: boolean;
  leadRecoveryPlanAllowed?: boolean;
  agentResumeBlocked: boolean;
  leadActionsExpanded: boolean;
  onLeadActionsExpandedChange: (expanded: boolean) => void;
  onAssign: (memberIds: string[], memberLabels?: string[]) => void;
  onPause: (conversationId: string) => void;
  onResume: (conversationId: string) => void;
  onMaybeClose: (conversationId: string, preserveAssignees?: boolean) => void;
  onReopen: (conversationId: string) => void;
  onOpenLeadDetails: () => void;
  onSummarize: (conversationId: string) => void;
  onLeadRecovery?: (conversationId: string) => void;
  onToggleFollowup: (conversationId: string, enabled: boolean) => void;
  onClearContext: (conversationId: string) => void;
  onDelete: (conversationId: string, leadName: string) => void;
  onAgentResumeBlocked: () => void;
  resolveEscalationButton: React.ReactNode;
  resolveEscalationButtonCompact?: React.ReactNode;
};

export function ConversationIaToggle({
  conversation,
  agentResumeBlocked,
  onPause,
  onResume,
  onAgentResumeBlocked,
}: Pick<
  ConversationChatHeaderActionsProps,
  "conversation" | "agentResumeBlocked" | "onPause" | "onResume" | "onAgentResumeBlocked"
>) {
  return (
    <div
      className="flex shrink-0 items-center gap-1 rounded-md px-0.5"
      title={
        conversation.state === "active"
          ? "Agente IA ativo — toque para pausar"
          : "Agente IA pausado — toque para ativar"
      }
    >
      <span className="text-[11px] font-medium text-muted-foreground">IA</span>
      <Switch
        checked={conversation.state === "active"}
        disabled={agentResumeBlocked && conversation.state !== "active"}
        onCheckedChange={(checked) => {
          if (checked) {
            if (agentResumeBlocked) {
              onAgentResumeBlocked();
              return;
            }
            onResume(conversation.conversation_id);
          } else {
            onPause(conversation.conversation_id);
          }
        }}
        className="h-5 w-9 shrink-0 data-[state=checked]:bg-primary"
      />
    </div>
  );
}

const IaToggle = ConversationIaToggle;

function ConversationOverflowMenu({
  conversation,
  isSummarizing,
  isLeadRecovering = false,
  leadRecoveryPlanAllowed = false,
  onOpenLeadDetails,
  onSummarize,
  onLeadRecovery,
  onToggleFollowup,
  onMaybeClose,
  onReopen,
  onClearContext,
  onDelete,
  compact = false,
}: Pick<
  ConversationChatHeaderActionsProps,
  | "conversation"
  | "isSummarizing"
  | "isLeadRecovering"
  | "leadRecoveryPlanAllowed"
  | "onOpenLeadDetails"
  | "onSummarize"
  | "onLeadRecovery"
  | "onToggleFollowup"
  | "onMaybeClose"
  | "onReopen"
  | "onClearContext"
  | "onDelete"
> & { compact?: boolean }) {
  const { toast } = useToast();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size={compact ? "icon" : "sm"}
          className={cn(
            "h-7 shrink-0 sm:h-8",
            compact ? "w-7 sm:w-8" : "px-2 text-xs sm:px-2.5",
          )}
        >
          <MoreVertical className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
          {!compact && <span className="ml-1.5 hidden lg:inline">Menu</span>}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="z-50 w-56 bg-background">
        <DropdownMenuItem onSelect={() => setTimeout(() => onOpenLeadDetails(), 0)}>
          <User className="mr-2 h-4 w-4" />
          Informações do Lead
        </DropdownMenuItem>
        <DropdownMenuItem
          onSelect={(e) => {
            e.preventDefault();
            setTimeout(() => onSummarize(conversation.conversation_id), 0);
          }}
          disabled={isSummarizing}
        >
          <Sparkles className="mr-2 h-4 w-4" /> {isSummarizing ? "Resumindo..." : "Resumir Atendimento"}
        </DropdownMenuItem>
        {onLeadRecovery && (
          <DropdownMenuItem
            onSelect={(e) => {
              e.preventDefault();
              setTimeout(() => onLeadRecovery(conversation.conversation_id), 0);
            }}
            disabled={isLeadRecovering}
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            {isLeadRecovering
              ? "Analisando..."
              : leadRecoveryPlanAllowed
                ? "Recuperar Lead"
                : "Recuperar Lead (Plus)"}
          </DropdownMenuItem>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={() => onToggleFollowup(conversation.conversation_id, conversation.followup_enabled)}
        >
          {conversation.followup_enabled ? (
            <>
              <BellOff className="mr-2 h-4 w-4" />
              Desativar Follow-up
            </>
          ) : (
            <>
              <Bell className="mr-2 h-4 w-4" />
              Ativar Follow-up
            </>
          )}
        </DropdownMenuItem>
        <DropdownMenuItem
          onSelect={() => {
            setTimeout(() => {
              void onMaybeClose(conversation.conversation_id);
            }, 0);
          }}
        >
          <X className="mr-2 h-4 w-4" />
          Encerrar Conversa
        </DropdownMenuItem>
        {!conversation.is_closed && (
          <DropdownMenuItem
            onSelect={() => {
              setTimeout(() => {
                void onMaybeClose(conversation.conversation_id, true);
              }, 0);
            }}
          >
            <CheckCircle className="mr-2 h-4 w-4" />
            Encerrar sem desatribuir
          </DropdownMenuItem>
        )}
        <DropdownMenuItem
          onSelect={() => setTimeout(() => onClearContext(conversation.conversation_id), 0)}
          className="text-destructive"
        >
          Limpar Contexto
        </DropdownMenuItem>
        {conversation.channel === "instagram" && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={async () => {
                try {
                  const { error } = await supabase.functions.invoke("update-instagram-user-data", {
                    body: { leadId: conversation.lead_id },
                  });
                  if (error) throw error;
                  toast({
                    title: "✅ Dados atualizados",
                    description: "Os dados do Instagram foram atualizados com sucesso",
                  });
                  window.location.reload();
                } catch (error: unknown) {
                  toast({
                    title: "❌ Erro ao atualizar",
                    description: error instanceof Error ? error.message : "Não foi possível atualizar os dados",
                    variant: "destructive",
                  });
                }
              }}
            >
              Atualizar dados do Instagram
            </DropdownMenuItem>
          </>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onSelect={() => setTimeout(() => onDelete(conversation.conversation_id, conversation.lead_name), 0)}
          className="text-destructive"
        >
          <Trash2 className="mr-2 h-4 w-4" />
          Excluir Conversa
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function ConversationChatHeaderExpandPanel({
  conversation,
  workspaceId,
  memberId,
  onAssign,
  onMaybeClose,
  onReopen,
  resolveEscalationButton,
}: Pick<
  ConversationChatHeaderActionsProps,
  "conversation" | "workspaceId" | "memberId" | "onAssign" | "onMaybeClose" | "onReopen"
> & { resolveEscalationButton?: React.ReactNode }) {
  return (
    <div className="flex min-w-0 flex-wrap items-center gap-1.5 border-t border-border/50 pt-2">
      {resolveEscalationButton}
      <MemberAssignPopover
        workspaceId={workspaceId}
        leadId={conversation.lead_id}
        currentMemberIds={conversation.assigned_member_ids}
        onAssign={onAssign}
        conversationId={conversation.conversation_id}
        actingMemberId={memberId}
      />
      <ConversationLeadFunnelControls leadId={conversation.lead_id} workspaceId={workspaceId} />
      <LeadTagsPopover workspaceId={workspaceId} leadId={conversation.lead_id} />
      {conversation.is_closed ? (
        <Button
          variant="ghost"
          size="sm"
          className="h-7 shrink-0 px-2 text-xs sm:h-8 sm:px-2.5"
          onClick={() => onReopen(conversation.conversation_id)}
        >
          <RotateCcw className="h-3.5 w-3.5 sm:mr-1.5 sm:h-4 sm:w-4" />
          Reabrir
        </Button>
      ) : (
        <Button
          variant="ghost"
          size="sm"
          className="h-7 shrink-0 px-2 text-xs sm:h-8 sm:px-2.5"
          onClick={() => onMaybeClose(conversation.conversation_id)}
        >
          <CheckCircle className="h-3.5 w-3.5 sm:mr-1.5 sm:h-4 sm:w-4" />
          Encerrar
        </Button>
      )}
      {conversation.is_closed && (
        <Badge variant="secondary" className="h-6 shrink-0 border-0 bg-muted/80 px-1.5 text-[11px] font-normal text-muted-foreground">
          Encerrado
        </Badge>
      )}
    </div>
  );
}

export function ConversationChatHeaderActions({
  conversation,
  workspaceId,
  memberId,
  wide,
  isSummarizing,
  isLeadRecovering = false,
  leadRecoveryPlanAllowed = false,
  agentResumeBlocked,
  leadActionsExpanded,
  onLeadActionsExpandedChange,
  onAssign,
  onPause,
  onResume,
  onMaybeClose,
  onReopen,
  onOpenLeadDetails,
  onSummarize,
  onLeadRecovery,
  onToggleFollowup,
  onClearContext,
  onDelete,
  onAgentResumeBlocked,
  resolveEscalationButton,
  resolveEscalationButtonCompact,
}: ConversationChatHeaderActionsProps) {
  const closeOrReopenButton = conversation.is_closed ? (
    <Button
      variant="ghost"
      size="sm"
      className="h-7 shrink-0 px-2 text-xs sm:h-8 sm:px-2.5"
      onClick={() => onReopen(conversation.conversation_id)}
    >
      <RotateCcw className="h-3.5 w-3.5 sm:mr-1.5 sm:h-4 sm:w-4" />
      <span className="hidden sm:inline">Reabrir</span>
    </Button>
  ) : (
    <Button
      variant="ghost"
      size="sm"
      className="h-7 shrink-0 px-2 text-xs sm:h-8 sm:px-2.5"
      onClick={() => onMaybeClose(conversation.conversation_id)}
    >
      <CheckCircle className="h-3.5 w-3.5 sm:mr-1.5 sm:h-4 sm:w-4" />
      <span className="hidden sm:inline">Encerrar</span>
    </Button>
  );

  if (!wide) {
    return (
      <>
        {!leadActionsExpanded && (resolveEscalationButtonCompact ?? resolveEscalationButton)}
        <IaToggle
          conversation={conversation}
          agentResumeBlocked={agentResumeBlocked}
          onPause={onPause}
          onResume={onResume}
          onAgentResumeBlocked={onAgentResumeBlocked}
        />

        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 shrink-0 sm:h-8 sm:w-8"
          aria-label={leadActionsExpanded ? "Recolher opções do lead" : "Mais opções do lead"}
          aria-expanded={leadActionsExpanded}
          onClick={() => onLeadActionsExpandedChange(!leadActionsExpanded)}
        >
          <ChevronDown
            className={cn("h-4 w-4 transition-transform duration-200", leadActionsExpanded && "rotate-180")}
          />
        </Button>

        <ConversationOverflowMenu
          conversation={conversation}
          isSummarizing={isSummarizing}
          isLeadRecovering={isLeadRecovering}
          leadRecoveryPlanAllowed={leadRecoveryPlanAllowed}
          onOpenLeadDetails={onOpenLeadDetails}
          onSummarize={onSummarize}
          onLeadRecovery={onLeadRecovery}
          onToggleFollowup={onToggleFollowup}
          onMaybeClose={onMaybeClose}
          onReopen={onReopen}
          onClearContext={onClearContext}
          onDelete={onDelete}
          compact
        />
      </>
    );
  }

  return (
    <>
      {resolveEscalationButtonCompact ?? resolveEscalationButton}
      <LeadTagsPopover workspaceId={workspaceId} leadId={conversation.lead_id} />
      <MemberAssignPopover
        workspaceId={workspaceId}
        leadId={conversation.lead_id}
        currentMemberIds={conversation.assigned_member_ids}
        onAssign={onAssign}
        conversationId={conversation.conversation_id}
        actingMemberId={memberId}
      />
      <ConversationLeadFunnelControls leadId={conversation.lead_id} workspaceId={workspaceId} />
      {conversation.is_closed && (
        <Badge variant="secondary" className="h-6 shrink-0 border-0 bg-muted/80 px-1.5 text-[11px] font-normal text-muted-foreground">
          Encerrado
        </Badge>
      )}
      <IaToggle
        conversation={conversation}
        agentResumeBlocked={agentResumeBlocked}
        onPause={onPause}
        onResume={onResume}
        onAgentResumeBlocked={onAgentResumeBlocked}
      />
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>{closeOrReopenButton}</TooltipTrigger>
          <TooltipContent>
            {conversation.is_closed ? "Reabrir conversa" : "Encerrar conversa"}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      <ConversationOverflowMenu
        conversation={conversation}
        isSummarizing={isSummarizing}
        isLeadRecovering={isLeadRecovering}
        leadRecoveryPlanAllowed={leadRecoveryPlanAllowed}
        onOpenLeadDetails={onOpenLeadDetails}
        onSummarize={onSummarize}
        onLeadRecovery={onLeadRecovery}
        onToggleFollowup={onToggleFollowup}
        onMaybeClose={onMaybeClose}
        onReopen={onReopen}
        onClearContext={onClearContext}
        onDelete={onDelete}
      />
    </>
  );
}
